import { withFormik } from 'formik';
import * as yup from 'yup';
import SparkTelephonyDeployment from './SparkTelephonyDeployment';
import TelephonyDeploymentRequest from '../../../../shared/models/telephonyDeployment/TelephonyDeploymentRequest';
import { defaultTelephonyDeploymentSettings } from '../../../../shared/constants/telephonyConstants.tsx/defaultTelephonyDeploymentSettings';
import { AppendedSection } from './ApplicationManagerContent';
import * as voicifyApi from '../../../../shared/api';

// Maria's multilingual voice string (from voiceOptions.ts)
const SPANISH_TTS_VOICE = "voice_id=3liN8q8YoeB9Hk6AboKe|model_id=eleven_multilingual_v2|stability=0.8|similarity_boost=0.8|speed=.97";
const ENGLISH_MULTILINGUAL_TTS_VOICE = "voice_id=3liN8q8YoeB9Hk6AboKe|model_id=eleven_turbo_v2|speed=1.15|stability=0.8|similarity_boost=0.1";

export interface SparkTelephonyDeploymentProps {
    voiceOptions: any
    handlePreviousSection: () => void
    handleNextSection: () => void
    createTelephonyDeployment: (appId: string, telephonyDeploymentRequest: TelephonyDeploymentRequest) => Promise<any>
    applicationId: string
    environmentId: string
    appendedSections: AppendedSection[]
    telephonyDeploymentSection: AppendedSection
    setAppendedSections: (sections: AppendedSection[]) => void
    incompleteSections: any[]
    setIncompleteSections: (sections: any[]) => void
    getVoiceString: (voice: string) => string
    clearTelephonyDeploymentErrors: () => void
    setPageErrorText: (text: string) => void
    phoneNumberDeploymentErrorText: string
    setPhoneNumberDeploymentErrorText: (text: string) => void
    getTabs: (existingTelephonyDeployment?: boolean) => void
    setTabs: (tabs: any) => void
    loadTelephonyDeployments: () => void
    handleRemoveSection: (sectionToRemove: AppendedSection) => void
    removeSectionFromIncompleteSections: (sectionToRemove: AppendedSection) => void
    assistantType?: string
    orgName?: string
    appName?: string
    onMultilingualEnabled?: () => void
}

export interface SparkTelephonyDeploymentFormData {
    phoneNumberStartsWith: string
    callRecordingEnabled: boolean
    ttsVoice: string
    useVirtualNumber: boolean
    virtualNumber?: string
    addSpanishLine: boolean
}

const getDefaultVoice = (assistantType?: string): string => {
    if (assistantType === 'ai-ordering') return 'Vicky';
    if (assistantType === 'dental-scheduling') return 'Callie';
    return 'female1';
};

const SparkTelephonyDeploymentForm = withFormik<SparkTelephonyDeploymentProps, SparkTelephonyDeploymentFormData>({
    mapPropsToValues: props => (
        {
            phoneNumberStartsWith: '',
            callRecordingEnabled: true,
            ttsVoice: getDefaultVoice(props.assistantType),
            useVirtualNumber: false,
            virtualNumber: '',
            addSpanishLine: false
        }
    ),
    validationSchema: yup.object().shape({}),
    handleSubmit: async (values, { props }) => {

        // Build the en-US deployment name
        const enUsName = (props.orgName && props.appName)
            ? `${props.orgName} - ${props.appName} - EN`
            : defaultTelephonyDeploymentSettings.name;

        const isDental = props.assistantType === 'dental-scheduling';
        const isOrdering = props.assistantType === 'ai-ordering';

        const telephonyDeploymentRequest: TelephonyDeploymentRequest = {
            environmentId: props.environmentId,
            noTracking: defaultTelephonyDeploymentSettings.noTracking,
            phoneNumberStartsWith: values.useVirtualNumber ? null : (values.phoneNumberStartsWith ?? defaultTelephonyDeploymentSettings.phoneNumberStartsWith),
            ttsOptions: [{
                ttsProvider: defaultTelephonyDeploymentSettings.ttsOptions[0].ttsProvider,
                ttsVoice: values.addSpanishLine
                    ? ENGLISH_MULTILINGUAL_TTS_VOICE
                    : (props.getVoiceString(values.ttsVoice) ?? defaultTelephonyDeploymentSettings.ttsOptions[0].ttsVoice)
            }],
            locale: defaultTelephonyDeploymentSettings.locale,
            asrEndpointing: defaultTelephonyDeploymentSettings.asrEndpointing,
            asrProvider: defaultTelephonyDeploymentSettings.asrProvider,
            // When adding Spanish line, upgrade en-US to nova-3
            asrModel: values.addSpanishLine ? 'nova-3' : defaultTelephonyDeploymentSettings.asrModel,
            asrTier: defaultTelephonyDeploymentSettings.asrTier,
            sonioxContextJson: defaultTelephonyDeploymentSettings.sonioxContextJson,
            performLanguageDetection: defaultTelephonyDeploymentSettings.performLanguageDetection,
            dtmfTurnsToListen: values.addSpanishLine ? 2 : defaultTelephonyDeploymentSettings.dtmfTurnsToListen,
            ttsPronunciationsMap: defaultTelephonyDeploymentSettings.ttsPronunciationsMap,
            firstMessageDelayMS: defaultTelephonyDeploymentSettings.firstMessageDelayMS,
            mergeTurnsAfterPauses: defaultTelephonyDeploymentSettings.mergeTurnsAfterPauses,
            mergeTurnTimeoutMS: defaultTelephonyDeploymentSettings.mergeTurnTimeoutMS,
            disableBargeIn: defaultTelephonyDeploymentSettings.disableBargeIn,
            repromptAttempts: defaultTelephonyDeploymentSettings.repromptAttempts,
            repromptDelaySeconds: defaultTelephonyDeploymentSettings.repromptDelaySeconds,
            name: enUsName,
            proactiveResponseDelayMS: isDental ? 1900 : defaultTelephonyDeploymentSettings.proactiveResponseDelayMS,
            proactiveResponses: defaultTelephonyDeploymentSettings.proactiveResponses,
            useProactiveResponses: defaultTelephonyDeploymentSettings.useProactiveResponses,
            disableWelcomeMessageBargeIn: defaultTelephonyDeploymentSettings.disableWelcomeMessageBargeIn,
            disableSpeechStarted: defaultTelephonyDeploymentSettings.disableSpeechStarted,
            speechStartedUnconfirmedBargeInLimit: defaultTelephonyDeploymentSettings.speechStartedUnconfirmedBargeInLimit,
            speechStartedPauseMS: defaultTelephonyDeploymentSettings.speechStartedPauseMS,
            speechStartedRequiredBufferCount: defaultTelephonyDeploymentSettings.speechStartedRequiredBufferCount,
            callRecordingEnabled: values.callRecordingEnabled ?? defaultTelephonyDeploymentSettings.callRecordingEnabled,
            speechStartedLevel: defaultTelephonyDeploymentSettings.speechStartedLevel,
            virtualNumber: values.useVirtualNumber && defaultTelephonyDeploymentSettings.virtualNumber,
            useVirtualNumber: values.useVirtualNumber,
            useGenerativeProactiveResponse: defaultTelephonyDeploymentSettings.useGenerativeProactiveResponse,
            generativeProactiveResponsePrompt: isDental
                ? '{{constant.generative_proactive_response_dental}}'
                : isOrdering
                    ? '{{constant.proactive_response_prompt}}'
                    : defaultTelephonyDeploymentSettings.generativeProactiveResponsePrompt,
            lastProactiveResponsesToDisallow: defaultTelephonyDeploymentSettings.lastProactiveResponsesToDisallow,
            notificationEmails: defaultTelephonyDeploymentSettings.notificationEmails,
            platform: defaultTelephonyDeploymentSettings.platform,
            postProactiveResponsePauseDelayMS: isDental ? 500 : defaultTelephonyDeploymentSettings.postProactiveResponsePauseDelayMS,
        };

        const createTelephonyDeploymentResponse = await props.createTelephonyDeployment(props.applicationId, telephonyDeploymentRequest);

        if (createTelephonyDeploymentResponse?.resultType === 'Ok' && createTelephonyDeploymentResponse.data) {

            // Run Spanish line automation if the toggle is on
            if (values.addSpanishLine) {
                const provisionedPhoneNumber = createTelephonyDeploymentResponse.data.phoneNumber;

                try {
                    // Step 1: Fetch all languages to get en-US and es-US IDs
                    const languagesResult = await voicifyApi.getAllLanguages();
                    if (!languagesResult || languagesResult.resultType !== 'Ok' || !languagesResult.data) {
                        props.setPageErrorText("Phone number created, but Spanish line setup failed: could not fetch language list.");
                    } else {
                        const enUsLanguage = languagesResult.data.find(l => l.shortCode === 'en-US');
                        const esUsLanguage = languagesResult.data.find(l => l.shortCode === 'es-US');

                        if (!enUsLanguage || !esUsLanguage) {
                            props.setPageErrorText("Phone number created, but Spanish line setup failed: could not find en-US or es-US language.");
                        } else {
                            // Step 2: Add es-US to the application's languages
                            try {
                                await voicifyApi.updateApplicationLanguages(props.applicationId, [enUsLanguage.id, esUsLanguage.id]);
                            } catch {
                                props.setPageErrorText("Phone number created, but Spanish line setup failed at language update step.");
                            }

                            // Step 3: Sync NLP and interaction model for es-US using en-US as source
                            try {
                                await voicifyApi.syncNLPAndInteractionModel(props.applicationId, [esUsLanguage.id], enUsLanguage.id);
                            } catch {
                                props.setPageErrorText("Phone number created, but Spanish line setup failed at NLP sync step.");
                            }

                            // Step 4: Queue NLP deployments to trigger interaction model rebuild
                            try {
                                await voicifyApi.queueDeployments(props.applicationId);
                            } catch {
                                props.setPageErrorText("Phone number created, but Spanish line setup failed at NLP queue step.");
                            }

                            // Step 5: Add es-US locale config to the same phone number.
                            // Mirrors what the UI does in handleMultiLocaleSubmit (TelephonyDeployment.tsx):
                            // spreads the existing deployment (which includes phoneNumber as an extra field),
                            // sets phoneNumberStartsWith to the full provisioned number so the server
                            // attaches this locale to the existing number instead of provisioning a new one,
                            // and sets phoneNumberCountryCode from the existing deployment.
                            const esUsName = (props.orgName && props.appName)
                                ? `${props.orgName} - ${props.appName} - ES`
                                : '';

                            const esUsRequest: TelephonyDeploymentRequest = {
                                ...createTelephonyDeploymentResponse.data,
                                phoneNumberStartsWith: provisionedPhoneNumber,
                                locale: 'es-US',
                                asrModel: 'nova-2',
                                performLanguageDetection: false,
                                dtmfTurnsToListen: 0,
                                name: esUsName,
                                ttsOptions: [{
                                    ttsProvider: 'ElevenLabs',
                                    ttsVoice: SPANISH_TTS_VOICE
                                }],
                                proactiveResponses: 'Un momento|Veamos|Solo un momento|Espera un segundo|Espera mientras reviso eso|Dame un momento|Espera mientras miro eso',
                                followUpProactiveResponses: 'Todavía estoy revisando eso | Sigo investigando esto por ti | Estoy trabajando activamente en esto y te actualizaré pronto | Todavía estoy buscando | Estoy avanzando con esto y te mantendré informado',
                                useGenerativeProactiveResponse: true,
                                generativeProactiveResponsePrompt: isOrdering
                                    ? '{{constant.generative_proactive_response_ordering_spanish}}'
                                    : '{{constant.generative_proactive_response_dental_spanish}}',
                                maxProactiveResponseAttempts: 3,
                            };

                            try {
                                await props.createTelephonyDeployment(props.applicationId, esUsRequest);
                            } catch {
                                props.setPageErrorText("Phone number created, but Spanish line deployment failed. Please try adding it manually.");
                            }
                        }
                    }
                } catch {
                    props.setPageErrorText("Phone number created, but Spanish line setup encountered an unexpected error.");
                }

                props.onMultilingualEnabled?.();
            }

            await props.loadTelephonyDeployments();

            const updatedTabs = props.getTabs(true);

            props.setTabs(updatedTabs);

            const copyAppendedSections = [...props.appendedSections];

            const updatedSections = copyAppendedSections.filter(section => section.id !== props.telephonyDeploymentSection.id);

            props.setAppendedSections(updatedSections);
        };

        if (createTelephonyDeploymentResponse?.resultType !== 'Ok') {
            const incompleteTelephonyDeploymentSection = { ...props.telephonyDeploymentSection, isComplete: false };
            if (createTelephonyDeploymentResponse.errors[0] === "No numbers found") {
                props.setPageErrorText("Uh oh! It looks like the area code you chose isn't available. Please save your changes, exit, and reopen the form trying a different one.");
            } else {
                props.setPageErrorText("Uh oh! It looks like there was a problem creating your phone number. Please save your changes, exit, and try again in a few minutes.");
            }
            const telephonyDeploymentSectionIndex = props.incompleteSections?.findIndex((section) => {
                return section.id === incompleteTelephonyDeploymentSection.id
            });
            const incompleteSectionsCopy = [...props.incompleteSections];
            if (telephonyDeploymentSectionIndex !== -1) {
                incompleteSectionsCopy[telephonyDeploymentSectionIndex] = incompleteTelephonyDeploymentSection;
                props.setIncompleteSections(incompleteSectionsCopy);
            } else {
                props.setIncompleteSections([...incompleteSectionsCopy, incompleteTelephonyDeploymentSection]);
            }
        }
    }
})(SparkTelephonyDeployment);


export default SparkTelephonyDeploymentForm;
